/* =============================================================
   NOTIFICATION PULSE

   One signal, published whenever the bell learns something new.

   The feedback widget has to refresh "every time an attendance
   notification comes in", and there is no socket event for
   notifications -- NotificationBell polls a count every 60s. Rather
   than give the widget a second poller on its own clock (two timers,
   two request streams, and a widget that updates a minute after the
   bell that announced it), the bell publishes here and the widget
   listens.

   Deliberately not a React context: the publisher is a component
   mounted in the topbar and the subscriber is a component mounted at
   the app root, so a provider wrapping both would have to sit above
   the whole tree to carry a signal that neither renders.

   `type` is carried so a listener can ignore pulses it does not care
   about -- the widget only reacts to attendance.
   ============================================================= */

const listeners = new Set();

/**
 * @param {(pulse: { type: string, at: number }) => void} listener
 * @returns {() => void} unsubscribe
 */
export const subscribeToNotifications = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Say that notifications changed.
 *
 * `type` is the notification type when one is known ('attendance_marked'
 * and so on), or 'unknown' when all the bell learned is that the unread
 * count moved -- which is all a count endpoint can tell it.
 */
export const pulseNotifications = (type = 'unknown', extra = {}) => {
  // 'removed' carries the withdrawn notifications' `ids`.
  const pulse = { ...extra, type: String(type || 'unknown'), at: Date.now() };
  for (const listener of listeners) {
    try {
      listener(pulse);
    } catch (error) {
      // One bad listener must not stop the others being told.
      console.error('Notification pulse listener failed:', error);
    }
  }
};
